import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { useHistory } from "react-router-dom";
import jsbridge from "@xpeng/js-bridge";
import request from '../../../../network/request'
import useBaseInfo from '../../store/useBaseInfo'
import useBasicFormData from '../../store/useBasicFormData';
import usePickupAddress from '../../store/usePickupAddress'
import DateSlider from '../DateSlider';
import useSelfArrivalCity from "../../store/useSelfArrivalCity";
import { DATE_RANGE_STORE_ITEM, ReservationItemTypes } from '../../interfaces/home';
import { getHumanizeDateText } from '../../../../utils/date';
import { getDistanceText } from "../../../../utils/common";
import { CommonLoadingComp } from '../../../../components/CommonLoading'
import EmptyPlaceHolder from '../../components/EmptyPlaceHolder';
import { sendReport } from '../../../../utils/reporter';
import ScoreLine from '../ScoreLine';
import { Tabs, Tab, TabList } from 'react-tabs';
import classnames from 'classnames';
import { ORDER_TAB_LIST, ORDER_TAB_MAP, TAB_ID_SET } from '../../config/constants';
import dayjs from 'dayjs';
import './index.scss';
import { ReactComponent as PositionIcon } from '../../../../assets/svgicons/position.svg'
import { ErrorPlaceholder } from '../../../../components/placeholder';
import { findServiceItemsParam } from '../../utils';

export interface ReserveByDatePanelProps {
  reserveItems: ReservationItemTypes[];
}

export default function ReserveByDatePanel(props: ReserveByDatePanelProps) {
  const { reserveItems = [] } = props;
  const history = useHistory();
  const { baseInfo, setContentEdited } = useBaseInfo();
  const {
    basicFormData,
    setDatesStoreContentOfDateTab,
    setStoreContentOfDateTab,
    setArriveStoreTimeByDateTab,
    setContactInfoData,
  } = useBasicFormData();
  const { resetPickupAddress } = usePickupAddress()
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);
  const { selfArrivalCity } = useSelfArrivalCity();
  const [curSelectedDateItem, setCurSelectedDateItem] = useState<null | DATE_RANGE_STORE_ITEM>(null);
  const isErrorLoadingRef = useRef(false);
  const loadingShopListRef = useRef(false);
  const [curTab, setCurTab] = useState<number>(ORDER_TAB_MAP.COMPREHENSIVE);
  
  const initData = useCallback((datesList) => {
    let firstHasCapacityIndex = null;
    const rangeList = datesList.map((dateItem, index) => {
      const { timestamp, hasCapacity, setCapacity } = dateItem;
      const dayObj = dayjs(timestamp);
      if (hasCapacity && firstHasCapacityIndex === null) firstHasCapacityIndex = index;
      return {
        ...dateItem,
        shopDetailResps: [],
        checked: index === 0 ? true : false,
        reserveQuota: hasCapacity ? null : 0,
        setCapacity,
        weekDate: getHumanizeDateText(dayObj),
        dateDesc: dayObj.format('MM/DD')
      }
    });

    // 编辑模式下，不重置，编辑过则重置
    if (!baseInfo.isEditingMode || baseInfo.contentEdited) {
      setContactInfoData({
        pickupServiceTime: ''
      });
      resetPickupAddress();
    }

    return { rangeList, firstHasCapacityIndex: firstHasCapacityIndex ?? 0 };
  }, [baseInfo.contentEdited, baseInfo.isEditingMode, resetPickupAddress, setContactInfoData]);

  // 根据日期获取门店列表数据
  const getListByDate = useCallback(async (date, currentTab = curTab) => {
    if (!date) return [];
    const locationItem = JSON.parse(localStorage.getItem("current_lng_and_lat") || '{}');
    // 获取 serviceItems 参数
    const { serviceItems, eventCode } = findServiceItemsParam(reserveItems);
    try {
      const list: any[] = await request(
        '/public/v2/module/maintenance/v2/shop/listShopByDate',
        {
          cityCode: selfArrivalCity?.cityCode,
          vin: baseInfo.frameNumber,
          reserveId: baseInfo.reserveId || '', // 修改预约单时必传
          sort: currentTab === 0 ? 1 : 2, // 排序类型，1为综合排序，2为距离优先，默认按综合排序
          reserveDate: date,
          serviceItems,
          eventCode,
          ...(selfArrivalCity?.address
            ? {
                lng: locationItem?.longitude ?? null,
                lat: locationItem?.latitude ?? null,
              }
            : {}),
        },
        'POST',
        {
          useCustomUrl: true,
        }
      )
      return Array.isArray(list) ? (list || []) : [];
    } catch (err) {
      setIsError(true);
      setErrorMsg(err.message);
      throw err;
    }
  }, [baseInfo.frameNumber, baseInfo.reserveId, curTab, selfArrivalCity, reserveItems]);

  /**
   * allData: 全局数据
   * curDate：选择日期进行数据请求
   * currentTab：排序切换
   * asyncList：已有数据则不发起请求，但做逻辑处理
   */
  const getListData = useCallback(async ({ allData, curDate, currentTab = curTab, asyncList = [], defaultTimestamp, defaultShopCode }) => {
    try {
      setIsLoading(true);
      let thisData = JSON.parse(JSON.stringify(allData));
      let _curDate = curDate;
      let defaultDt = defaultTimestamp ? dayjs(defaultTimestamp) : null;
      let dateKey = '';
      let hourMinuteKey = '';
      let isSetDefault = false;
      
      if (defaultDt && thisData?.[0]?.date && thisData?.[thisData?.length - 1]?.date) {
        const firstDateTimestamp = +dayjs(thisData[0].date);
        const lastDateTimestamp = +dayjs(thisData[thisData.length - 1]?.date).add(1, 'day');
        const defaultDateTimestamp = +defaultDt;
        if ((firstDateTimestamp <= defaultDateTimestamp) && (defaultDateTimestamp < lastDateTimestamp)) { // 默认的时间应该是在返回的时间里面，否则不进行默认
          isSetDefault = true;
          dateKey = defaultDt.format('YYYY-MM-DD');
          hourMinuteKey = defaultDt.format('HH:mm');
          _curDate = dateKey; 
        }
      }
      
      const index = thisData?.findIndex((v: any) => v?.date === _curDate);
      
      // 初次请求时，那怕没找到默认日期对应的数据，也设置顶部日期列
      if (index === -1) {
        setDatesStoreContentOfDateTab(thisData);
        setArriveStoreTimeByDateTab(null);
        return
      };

      let list: any = [];
      // asyncList: 有数据则不再发起请求，直接拿数据
      if (asyncList && !!asyncList?.length) {
        list = JSON.parse(JSON.stringify(asyncList));
      } else {
        list = await getListByDate(_curDate, currentTab);
      }
      // 每请求数据，将选择第一个门店可选择的当作选中
      let reserveQuota = 0; // 
      let hasSelCur = false;
      let hasOutSelIndex: any = null;
      let hasInSelIndex: any = null;
      list = list?.map((item, index) => {
        let canReserveNumber = 0;
        const timeList = item?.dates || [];
        if (timeList && !!timeList?.length && !item?.groupFive) {
          const newTimeList = timeList.map((_item, _index) => {
            if (_item?.canReserveNumber !== 0) {
              canReserveNumber = canReserveNumber + _item?.canReserveNumber;
              // 如果是有默认值
              if (isSetDefault) {
                if (defaultShopCode && item?.code === defaultShopCode) {
                  hasSelCur = true;
                  // 设置默认店
                  hasOutSelIndex = index;
                  // 设置默认时间段
                  if (_item?.startTime === hourMinuteKey) {
                    hasInSelIndex = _index;
                  }
                }
              } else {
                if (!hasSelCur) {
                  hasSelCur = true;
                  hasOutSelIndex = index;
                  hasInSelIndex = _index
                }
              }
              return {..._item, checked: (hasOutSelIndex === index && hasInSelIndex === _index) ? true : false}
            } else {
              return {..._item, checked: false}
            }
          })
          reserveQuota = reserveQuota + canReserveNumber;
          return {...item, canReserveNumber: canReserveNumber, dates: newTimeList, checked:hasOutSelIndex === index ? true : false}
        } else {
          return {...item, canReserveNumber: canReserveNumber, checked: false}
        }
      }, [])
      let selectIndex = index;
      thisData = thisData.map((item, _index) => {
        // 当前日期处理
        if (_index === index) {
          item['shopDetailResps'] = list;
          item['reserveQuota'] = reserveQuota;
          if (hasSelCur) item['checked'] = true;
          else item['checked'] = false;
        // 其他日期处理
        } else {
          // 说明已经重新选择，则清空其他
          if (hasSelCur) {
            item['checked'] = false; // 日期清空选择
            item['shopDetailResps'] = item?.shopDetailResps?.map((_item) => {
              _item['dates'] = _item?.dates?.map(__item => ({...__item, checked: false})) // 具体时间段清空选择
              return { ..._item, checked: false }; // 门店清空选择
            })
          } else {
            if (item?.checked) selectIndex = _index;
          }
        }
        return item;
      })

      const matchingStoreItem = thisData[selectIndex]?.shopDetailResps?.find(v => v?.checked) || null;
      const matchingRuleItem: any = matchingStoreItem?.dates?.find(v => v?.checked) || null; // 匹配到的 RuleItem

      // 更新数据
      // 重置取送车时间
      setContactInfoData({
        pickupServiceTime: ''
      });
      resetPickupAddress();
      if (matchingRuleItem) {
        setArriveStoreTimeByDateTab({
          timestamp: thisData[selectIndex]?.timestamp,
          date: matchingRuleItem?.date,
          startTime: matchingRuleItem?.startTime,
        });
      } else {
        setArriveStoreTimeByDateTab(null);
      }
      setCurSelectedDateItem(thisData[selectIndex]); // 更新选中的列表
      setStoreContentOfDateTab(matchingStoreItem || null); // 更新选中的门店
      setDatesStoreContentOfDateTab(thisData); // 更新总数据
    } catch (error) {
      console.log(error); 
    } finally {
      setIsLoading(false);
    }
  }, [curTab, setStoreContentOfDateTab, setDatesStoreContentOfDateTab, setContactInfoData, resetPickupAddress, getListByDate, setArriveStoreTimeByDateTab])

  // 数据初始化
  const init = useCallback(async () => {
    try {
      // 获取 serviceItems 参数
      const { serviceItems, eventCode } = findServiceItemsParam(reserveItems);
      // 获取时间列表数据
      const dateList: any[] = await request(
        "/public/v2/module/maintenance/v2/shop/listReserveDate",
        {
          cityCode: selfArrivalCity?.cityCode,
          vin: baseInfo.frameNumber,
          serviceItems,
          eventCode,
          reserveId: baseInfo.reserveId || '', // 修改预约单时必传
        },
        "POST",
        {
          useCustomUrl: true
        }
      );
      // 初始化基本数据，除列表数据
      const {rangeList: datesListWithState, firstHasCapacityIndex} = initData(dateList);
      
      if (!datesListWithState) return;
      // 获取第一个有容量的门店数据列表
      await getListData({
        allData: datesListWithState,
        curDate: datesListWithState?.[firstHasCapacityIndex]?.date,
        defaultTimestamp: basicFormData.storeTimeStepData.modifyOrderArriveTime,
        defaultShopCode: basicFormData.storeTimeStepData.modifyOrderShopCode
      });
    } catch (error) {
      console.log('init error', error);
      isErrorLoadingRef.current = true;
      setIsError(true);
      setErrorMsg(error.message);
    } finally {
      setIsLoading(false);
      loadingShopListRef.current = false;
    }
  }, [baseInfo.frameNumber, baseInfo.reserveId, basicFormData.storeTimeStepData.modifyOrderArriveTime, basicFormData.storeTimeStepData.modifyOrderShopCode, reserveItems, getListData, initData, selfArrivalCity])

  useEffect(() => {
    // 重新计算选中的 date item
    if (!basicFormData.storeTimeStepData.datesStoreDataByDateTab) return;
    if (basicFormData.storeTimeStepData.datesStoreDataByDateTab.length === 0) {
      setShowEmpty(true);
    }
    const checkedDateItems = basicFormData.storeTimeStepData.datesStoreDataByDateTab.filter(item => {
      return !!item.checked;
    });
    
    if (checkedDateItems.length > 0) {
      setCurSelectedDateItem(checkedDateItems[0]);
    }
  }, [
    basicFormData.storeTimeStepData.datesStoreDataByDateTab
  ]);

  useEffect(() => { 
    
    if (!!curSelectedDateItem?.shopDetailResps && !!curSelectedDateItem?.shopDetailResps?.length) {
      setShowEmpty(false);
    } else {
      setShowEmpty(true);
    }
  }, [curSelectedDateItem])

  useEffect(() => {
    // 无 cityCode || 非当前 tab
    if (!selfArrivalCity.cityCode || loadingShopListRef.current || isErrorLoadingRef.current || basicFormData.storeTimeStepData.reserveType !== TAB_ID_SET.DATE_TAB) return;
    if (!basicFormData.storeTimeStepData.datesStoreDataByDateTab) {
      setIsLoading(true);
      loadingShopListRef.current = true;
      init();
    }
  }, [
    selfArrivalCity,
    init,
    basicFormData.storeTimeStepData.datesStoreDataByDateTab,
    basicFormData.storeTimeStepData.reserveType
  ]);

  const onDateItemChangeHandler = async (data) => {
    console.log('---- onDateItemChangeHandler ----', data);
    // hasCapacity 表示容量，没有容量则表示没有可选的，已满
    const { date: targetDate, hasCapacity } = data;
    if (!hasCapacity) return;
    const { date: curDate } = curSelectedDateItem || { date: '' };
    let curDatesList = basicFormData.storeTimeStepData?.datesStoreDataByDateTab || [];
    if (!curDatesList) return;
    const selIndex = curDatesList.findIndex(v => v?.date === targetDate);

    // 重置当前命中日期
    if (selIndex === -1) return;
    curDatesList = curDatesList.map((item, index) => {
      return {...item, checked: index === selIndex ? true : false};
    })
    setDatesStoreContentOfDateTab(curDatesList);

    // 点击相同日期不请求，和已有数据不请求    
    if (curDate === targetDate) return;

    // 获取当前日期的数据, 更新列表数据, selIndex是用户点击的日期，如果没数据，selectIndex 是真正被选中的上一个 index，
    await getListData({ allData:curDatesList, curDate: targetDate, asyncList: curDatesList[selIndex]?.shopDetailResps });
    
    // 重置取送车时间
    setContactInfoData({
      pickupServiceTime: ''
    });
    resetPickupAddress();
    setContentEdited(true);
  }

  const onStoreSelected = (item, option) => {
    if (!curSelectedDateItem) return;

    const { timestamp, date } = curSelectedDateItem;
    const { shopCode: targetShopCode, timeOption: timeOptionKey, startTime } = option;

    const curDatesList = basicFormData.storeTimeStepData?.datesStoreDataByDateTab || [];
    let matchingRuleItem: any = null;
    const updatedDatesList = curDatesList.map(dateItem => {
      return {
        ...dateItem,
        shopDetailResps: dateItem.shopDetailResps ? dateItem.shopDetailResps.map((storeItem: any) => {
          let checkedOption = false;
          const storeChecked = storeItem.code === targetShopCode;
          return {
            ...storeItem,
            checked: storeChecked,
            dates: storeItem?.dates?.map(ruleItem => {
              // code-day-hour-min
              const { date, startTime, canReserveNumber } = ruleItem;
              const { code: curShopCode } = storeItem;
              // const dhm = `${date}-${startTime}`;
              if (timeOptionKey) {
                const dhm = `${curShopCode}-${date}-${startTime}`;
                return {
                  ...ruleItem,
                  checked: dhm === timeOptionKey
                }
              }
              
              // 切换日期，选中默认 option
              // console.log('!checkedOption && storeChecked && canReserveNumber > 0', !checkedOption, storeChecked, canReserveNumber > 0);
              const isCheckedOption = !checkedOption && storeChecked && canReserveNumber > 0;
              checkedOption = checkedOption || isCheckedOption;
              if (isCheckedOption) {
                matchingRuleItem = ruleItem;
              }
              return {
                ...ruleItem,
                checked: isCheckedOption
              }
            })
          }
        }) : []
      }
    })
    setStoreContentOfDateTab(item);
    setDatesStoreContentOfDateTab(updatedDatesList);

    // 重置取送车时间
    setContactInfoData({
      pickupServiceTime: ''
    });
    resetPickupAddress();
  
    setArriveStoreTimeByDateTab({
      timestamp: timestamp,
      date: date,
      startTime: startTime || matchingRuleItem?.startTime,
    });
    setContentEdited(true);
  }

  const onTapSwitchCity = () => {
    history.push(`${history.location.pathname}${history.location.search}#select-cities`);
  }

  // 综合排序和距离优先切换
  const changeSort = useCallback(async (index, allData) => {
    setCurTab(index);
    await getListData({ allData, curDate: curSelectedDateItem?.date, currentTab: index })
  }, [curSelectedDateItem, getListData])

  const locaction = useMemo(() => {

    if (selfArrivalCity?.address) {
      return <div className='city-switch-field-address'>
        <PositionIcon color="#7BAE3F" />
        <div className="city-switch-field-text">{selfArrivalCity?.address}</div>
      </div>
    }
    return selfArrivalCity?.cityName || ''
  }, [selfArrivalCity]);

  const reload = useCallback(() => {
    setIsError(false);
    setErrorMsg('');
    init();
  }, [init]);

  if (isError) {
    return <ErrorPlaceholder message={errorMsg} getData={reload} />
  }

  return (
    <div className="reserve-date-panel">
      {
        basicFormData.storeTimeStepData.datesStoreDataByDateTab && basicFormData.storeTimeStepData.datesStoreDataByDateTab.length > 0 && (
          <div className="date-slider-wrapper">
            <DateSlider
              dateList={basicFormData.storeTimeStepData.datesStoreDataByDateTab}
              onSelectChanged={onDateItemChangeHandler}
            />
          </div>
        )
      }
      <div className="store-list-wrapper">
        <div className="title-field">
          <div className="title-text">可选门店</div>
          {
            // !!selfArrivalCity?.cityCode && (
            <div className="city-switch-field" onClick={onTapSwitchCity}>
              <div >{locaction}</div>
              <i className="switch-icon" />
            </div>
            // )
          }
        </div>
        <Tabs
          id="custom-nav-comp"
          selectedIndex={ORDER_TAB_MAP.COMPREHENSIVE}
          selectedTabClassName="active"
          onSelect={() => {}}
          forceRenderTabsPanel
        >
        <TabList>
          <div className="order-tab-list">
            {
              ORDER_TAB_LIST.map((tabItem, tabIndex) => {
                return (
                  <Tab key={tabItem.id}>
                    <div
                      className={classnames("tab-item", {
                        'selected-tab': tabIndex === curTab,
                      })}
                      onClick={() => {
                        if (tabIndex === curTab) return;
                        changeSort(tabIndex, basicFormData.storeTimeStepData.datesStoreDataByDateTab);
                      }}
                    >
                      {
                        tabItem.name
                      }
                    </div>
                  </Tab>
                )
              })
            }
          </div>
        </TabList>
      </Tabs>
        {!isLoading && !showEmpty && <div className="store-options-list">
          {
            curSelectedDateItem
            && curSelectedDateItem.shopDetailResps
            && curSelectedDateItem.shopDetailResps.map(shopItem => {
              return (
                <React.Fragment key={`${shopItem.date}-${shopItem.uuid}`}>
                  <StoreOptionCard
                    shopInfo={shopItem}
                    checked={!!shopItem.checked}
                    disabled={!!shopItem.groupFive || shopItem.canReserveNumber === 0}
                    onSelected={onStoreSelected}
                  />
                </React.Fragment>
              )
            })
          }
        </div>}
        {
          isLoading && (
            <div className="loading-field">
              <CommonLoadingComp />
            </div>
          )
        }
        {
          // 空状态
          !isLoading && showEmpty && (
            <div className="empty-field">
              <EmptyPlaceHolder />
            </div>
          )
        }
      </div>
    </div>
  )
}

function StoreOptionCard(props) {
  const { shopInfo, checked = false, disabled = false, onSelected = () => {} } = props;
  const [curSelectedTimeOpt, setCurSelectedTimeOpt] = useState('');
  const [curSelectedStartTime, setCurSelectedStartTime] = useState('');

  const onSelectTimeOption = target => {
    // console.log('onSelectTimeOption ~~~~~~~~~~~~~~>>> ', target);
    const { code: shopCode } = shopInfo
    const { date, startTime, canReserveNumber } = target;
    if (disabled || !canReserveNumber) return;
    const dhm = `${shopCode}-${date}-${startTime}`;
    setCurSelectedTimeOpt(dhm);
    setCurSelectedStartTime(startTime);
    onSelected(shopInfo, {
      shopCode,
      timeOption: dhm,
      startTime,
    });
  };

  const openMap = data => {
    const { name, lat, lot } = data;
    jsbridge.callHandler('navigate', {
        label: name,
        latitude: Number(lat),
        longitude: Number(lot),
      })
    sendReport({
      mcode: "门店列表查看导航点击",
      point: 'P11183`B013',
    });
  };

  return (
    <div
      className={classNames("store-option-item", {
        'store-disabled': disabled,
      })}
      onClick={e => {
        e.stopPropagation();
        if (disabled) return;
        onSelected(shopInfo, {
          shopCode: shopInfo.code,
          timeOption: curSelectedTimeOpt,
          startTime: curSelectedStartTime || '',
        });
      }}
    >
      <div className="option-radio-field">
        <i className={classNames("radio-icon ", {
          checked: checked
        })} />
      </div>
      <div className="main-content-field">
        <div className="store-title-field">
          {
            `${shopInfo.name || ''} `
          }
          {
            shopInfo?.groupFive && (
              <span className="suspend-label">
                暂停营业
              </span>
              )
          }
          {
            !!shopInfo.isUserDefaultShop && (
              <div className="spec-store-label">专属门店</div>
            )
          }
          {
            shopInfo?.canReserveNumber === 0 && (
              <div className="empty-quota-label">预约已满</div>
            )
          }
          {/* {
            shopInfo.tags && shopInfo.tags[0] === "常去" && (
              <div className="usual-store-label">常去门店</div>
            )
          } */}
        </div>
        <div className="addr-field">
          {
            shopInfo.distance ? `${getDistanceText(shopInfo.distance)}｜` : ''
          }
          {shopInfo.linkAddr}
        </div>
        <div className='score-line'>
          <ScoreLine scoreInfo={shopInfo?.score} />
        </div>
        
        {
          shopInfo?.shopTags?.length > 0 ? (
            <div className="labels-field">
              {
                shopInfo?.shopTags.map((tag, i) => {
                  return (
                    <div key={i} className="tag-item">{tag}{i === shopInfo.shopTags.length - 1 ? '' : '｜'}</div>
                  );
                })
              }
            </div>
          ) : null
        }
        {
          shopInfo?.groupFive && (
            <span className="suspend-desc">
              {shopInfo.groupFive}
            </span>
            )
        }
        {
          !shopInfo?.groupFive && (
            <div className="actions-field">
              {
                !!shopInfo.lat && !!shopInfo.lot && (
                  <div
                    className="nav-btn"
                    onClick={e => {
                      e.stopPropagation();
                      openMap({ name: shopInfo.name, lat: shopInfo.lat, lot: shopInfo.lot });
                    }}
                  />
                )
              }
              {
                shopInfo.linkTel && shopInfo.linkTel !== '0' && (
                  <a
                    href={`tel:${shopInfo.linkTel}`}
                    className='info-content'
                    onClick={e => {
                      e.stopPropagation();
                      sendReport({
                        mcode: "门店列表查看电话点击",
                        point: 'P11183`B014',
                      });
                    }}
                  >
                    <div className="phone-btn" />
                  </a>
                )
              }
            </div>
          )
        }
        {
          !shopInfo?.groupFive && shopInfo?.canReserveNumber !== 0 && (
            <div className="time-options-list">
              {
                shopInfo.dates && shopInfo.dates.map((tItem, i) => {
                  // code-day-hour-min
                  const dhm = `${shopInfo.code}-${tItem.date}-${tItem.startTime}`;
                  return (
                    <div
                      key={dhm}
                      className={classNames("time-option-item", {
                        // selected: dhm === curSelectedTimeOpt,
                        selected: !!tItem.checked,
                        disabled: tItem.canReserveNumber === 0,
                      })}
                      onClick={e => {
                        e.stopPropagation();
                        onSelectTimeOption(tItem)
                      }}
                    >
                      <span>{ tItem.startTime }</span>
                      <i className="selected-icon" />
                    </div>
                  );
                })
              }
            </div>
          )
        }
      </div>
    </div>
  )
}

